import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight, MapPin, Users, Calendar, Clock, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../shadcn/ui/dialog";
import { Badge } from "../../../../shadcn/ui/badge";
import { formatDurationType, formatDurationUnit } from "../../../../utils/bookingTimeframeUtils";

const SpaceDetailsModal = ({ isOpen, onClose, space }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen || !space) return null;

  // Get all images - handle different formats
  const getAllImages = () => {
    if (!space.images || space.images.length === 0) {
      // Fallback to single image if available
      if (space.image) {
        return [typeof space.image === "string" ? space.image : space.image?.url || space.image?.resourceUrl];
      }
      return [];
    }

    return space.images
      .map(img => {
        if (typeof img === "string") return img;
        return img?.url || img?.resourceUrl || img;
      })
      .filter(Boolean);
  };

  const images = getAllImages();
  const hasImages = images.length > 0;
  const hasMultipleImages = images.length > 1;

  const goToNext = () => {
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };

  const goToPrev = () => {
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const goToImage = index => {
    setCurrentImageIndex(index);
  };

  // Get category info
  const getCategoryInfo = category => {
    const normalized =
      category?.toUpperCase?.() ||
      (typeof category === "object" ? (category?.code || category?.name || "").toUpperCase() : "");
    switch (normalized) {
      case "WORK":
        return { label: "WORK", color: "bg-blue-100 text-blue-700" };
      case "STAY":
        return { label: "STAY", color: "bg-green-100 text-green-700" };
      case "PLAY":
        return { label: "PLAY", color: "bg-purple-100 text-purple-700" };
      default:
        return { label: "OTHER", color: "bg-gray-100 text-gray-700" };
    }
  };

  const categoryInfo = getCategoryInfo(space.category);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">{space.name}</DialogTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {space.category && <Badge className={categoryInfo.color}>{categoryInfo.label}</Badge>}
                {space.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-1" />
                    {space.location}
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </DialogHeader>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Image Carousel */}
          {hasImages ? (
            <div className="relative w-full h-64 md:h-96 bg-gray-100">
              <img
                src={images[currentImageIndex]}
                alt={space.name}
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.src = "/img/placeholder-workspace.jpg";
                }}
              />

              {/* Navigation Arrows */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={goToPrev}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-lg transition-all"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-700" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-lg transition-all"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-700" />
                  </button>
                </>
              )}

              {/* Image Indicators */}
              {hasMultipleImages && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex ? "bg-white w-6" : "bg-white bg-opacity-50"
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Image Counter */}
              {hasMultipleImages && (
                <div className="absolute top-4 right-4 px-2 py-1 bg-black bg-opacity-50 text-white text-xs rounded">
                  {currentImageIndex + 1} / {images.length}
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-64 md:h-96 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <Building2 className="w-16 h-16 text-gray-400" />
            </div>
          )}

          {/* Space Details */}
          <div className="p-6 space-y-6">
            {/* Description */}
            {space.description && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{space.description}</p>
              </div>
            )}

            {/* Key Information */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {space.capacity && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">Capacity</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{space.capacity}</p>
                </div>
              )}

              {space.ratePrice && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">Price</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {space.currency || "KES"} {(space.ratePrice || 0).toLocaleString()}
                    {space.duration && (
                      <span className="text-xs text-gray-500"> {formatDurationType(space.duration)}</span>
                    )}
                  </p>
                </div>
              )}

              {space.minimumDuration && space.maximumDuration && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">Duration</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {space.minimumDuration} - {space.maximumDuration}{" "}
                    {formatDurationUnit(space.duration) + (space.maximumDuration !== 1 ? "s" : "") || "hours"}
                  </p>
                </div>
              )}

              {space.instances && space.instances.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">Instances</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{space.instances.length}</p>
                </div>
              )}
            </div>

            {/* Amenities */}
            {space.amenities && space.amenities.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Amenities</h3>
                <div className="flex flex-wrap gap-2">
                  {space.amenities.map((amenity, index) => {
                    const amenityName = typeof amenity === "object" ? amenity.name || amenity.title : amenity;
                    return (
                      <Badge key={index} variant="outline" className="text-xs">
                        {amenityName}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Image Thumbnails */}
            {hasMultipleImages && images.length > 1 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">All Images</h3>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentImageIndex
                          ? "border-blue-500 ring-2 ring-blue-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${space.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={e => {
                          e.target.src = "/img/placeholder-workspace.jpg";
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpaceDetailsModal;
